import { useNavigate } from "react-router-dom";
import AppShell from "../components/ui/AppShell";
import SectionHeader from "../components/ui/SectionHeader";
import ActionCard from "../components/ui/ActionCard";
import BottomNav from "../components/ui/BottomNav";
import ServiceShortcutCard from "../components/home/ServiceShortcutCard";
import { allServicesIndex } from "../data/catalog/allServicesIndex";
import { professionalServices } from "../data/services/professionalServices";

function Home() {
  const navigate = useNavigate();
  const featuredServiceTitles = [
    "Labour",
    "Mason / Mistri",
    "Steel Rod & Wire Work",
    "Welder",
    "Plumber",
    "Electrician",
    "Carpenter",
    "Civil Engineer",
    "Architect",
    "Interior Designer",
    "AC Repair",
    "House Maid",
    "Cook / Chef",
    "Water Purifier Repair",
    "Materials",
    "Build Your House",
    "Insta Help",
  ];

  const buildServiceSubtitle = (service) =>
    service.tags && service.tags.length > 0
      ? service.tags.slice(0, 3).join(", ")
      : service.category;

  const featuredServices = featuredServiceTitles
    .map((title) =>
      allServicesIndex.find((service) => service.title === title) || null
    )
    .filter(Boolean);

  const featuredProfessionalServices = professionalServices.slice(0, 6);

  const homeCategoryOrder = [
    "Construction & Labour",
    "Repairs & Installation",
    "Appliance Services",
    "Home Help & Lifestyle",
    "Quick Help",
    "Marketplace",
    "Professional & Project Services",
  ];

  const servicesByCategory = homeCategoryOrder.map((category) => ({
    category,
    services: allServicesIndex
      .filter((service) => service.category === category)
      .sort((a, b) => {
        const featuredA = featuredServiceTitles.indexOf(a.title);
        const featuredB = featuredServiceTitles.indexOf(b.title);

        if (featuredA !== -1 && featuredB !== -1) return featuredA - featuredB;
        if (featuredA !== -1) return -1;
        if (featuredB !== -1) return 1;

        return a.title.localeCompare(b.title);
      }),
  }));

  const quickActions = [
    {
      icon: "📋",
      title: "Detailed Rate Cards",
      description: "See service-by-service pricing references before you book.",
      actionLabel: "Open Rate Cards",
      onClick: () => navigate("/customer-rate-card"),
    },
    {
      icon: "📦",
      title: "Buy Materials",
      description: "Browse material listings, compare options, and place orders directly.",
      actionLabel: "Open Materials",
      onClick: () => navigate("/materials"),
    },
    {
      icon: "📅",
      title: "My Bookings",
      description: "Track booked services, material orders, and upcoming work from one place.",
      actionLabel: "Open Bookings",
      onClick: () => navigate("/my-bookings"),
    },
  ];

  const constructionJourneys = [
    {
      icon: "🏗️",
      title: "Start Site Labour",
      description:
        "For manpower, helpers, shifting support, and everyday on-site execution needs.",
      actionLabel: "Open Labour",
      onClick: () => navigate("/labour"),
    },
    {
      icon: "🧱",
      title: "Concrete & Rebar Prep",
      description:
        "For rod busting, steel tying, slab preparation, and reinforcement-oriented work.",
      actionLabel: "Open Steel & Rod Work",
      onClick: () => navigate("/steel-rod-wire-work"),
    },
    {
      icon: "🔥",
      title: "Metal & Fabrication Jobs",
      description:
        "For welding, grill work, metal correction, fabrication, and structural support tasks.",
      actionLabel: "Open Fabrication Lane",
      onClick: () => navigate("/welder"),
    },
    {
      icon: "🏠",
      title: "Plan Full House Work",
      description:
        "For broader home construction planning, budgeting, and linked execution support.",
      actionLabel: "Plan Build",
      onClick: () => navigate("/build-your-house"),
    },
    {
      icon: "AR",
      title: "Plan With Professionals",
      description:
        "For architect, engineer, BOQ, supervision, approvals, and larger project support that goes beyond one-off labor booking.",
      actionLabel: "Open Professional Services",
      onClick: () => navigate("/architect"),
    },
  ];

  return (
    <AppShell maxWidth="1260px">
      <div style={heroCard}>
        <div style={heroGrid} className="home-hero-grid">
          <div>
            <div style={heroBadge}>MistriBazaar</div>
            <h1 style={heroTitle} className="home-hero-title">Book what you need</h1>

            <div style={heroActions} className="home-hero-actions">
              <button
                onClick={() => navigate("/labour")}
                style={solidBtn}
                className="home-hero-action-button"
              >
                Book labour
              </button>

              <button
                onClick={() => navigate("/materials")}
                style={solidBtn}
                className="home-hero-action-button"
              >
                Buy materials
              </button>

              <button
                onClick={() => navigate("/build-your-house")}
                style={outlineBtn}
                className="home-hero-action-button"
              >
                Plan your house
              </button>
            </div>
          </div>

          <div style={heroShortcutGrid} className="home-hero-shortcut-grid">
            {featuredServices.slice(0, 6).map((item) => (
              <button
                key={item.title}
                onClick={() => navigate(item.path)}
                style={heroShortcutBtn}
                className="home-hero-shortcut-button"
              >
                <span style={heroShortcutIcon}>{item.icon}</span>
                <span style={heroShortcutLabel}>{item.title}</span>
              </button>
            ))}

            {featuredProfessionalServices.slice(0, 2).map((item) => (
              <button
                key={item.title}
                onClick={() => navigate(item.path)}
                style={heroShortcutBtn}
                className="home-hero-shortcut-button"
              >
                <span style={heroShortcutIcon}>{item.icon}</span>
                <span style={heroShortcutLabel}>{item.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <SectionHeader
        title="Construction & Labour"
        subtitle="Start here for site work."
        style={{ marginTop: "34px" }}
      />

      <div style={constructionGrid}>
        {featuredServices.slice(0, 6).map((item) => (
          <ServiceShortcutCard
            key={item.title}
            icon={item.icon}
            title={item.title}
            subtitle={buildServiceSubtitle(item)}
            tags={item.tags}
            onClick={() => navigate(item.path)}
          />
        ))}
      </div>

      <SectionHeader
        title="Professional & Project Services"
        subtitle="Book architect, engineer, BOQ, supervision, approvals, waterproofing, solar, rental, and project-led specialists from the homepage."
        style={{ marginTop: "34px" }}
      />

      <div style={professionalHighlightCard}>
        <div
          style={professionalHighlightHeader}
          className="home-professional-highlight-header"
        >
          <div>
            <div style={professionalLabel}>New construction operating layer</div>
            <h3 style={professionalTitle} className="home-professional-title">
              Services for planning, coordination, approvals, and larger project execution
            </h3>
          </div>

          <button
            onClick={() => navigate("/all-services")}
            style={professionalBtn}
            className="home-professional-button"
          >
            View all services
          </button>
        </div>

        <div style={professionalGrid}>
          {featuredProfessionalServices.map((item) => (
            <ServiceShortcutCard
              key={item.title}
              icon={item.icon}
              title={item.title}
              subtitle={item.badge}
              tags={item.bookingModes}
              onClick={() => navigate(item.path)}
            />
          ))}
        </div>
      </div>

      <SectionHeader
        title="Start by work type"
        subtitle="Pick the kind of job."
        style={{ marginTop: "34px" }}
      />

      <div style={journeyGrid}>
        {constructionJourneys.map((item) => (
          <ActionCard
            key={item.title}
            icon={item.icon}
            title={item.title}
            description={item.description}
            actionLabel={item.actionLabel}
            onClick={item.onClick}
          />
        ))}
      </div>

      <SectionHeader
        title="Explore Every Service"
        subtitle="Browse services directly from the homepage so customers do not need to search manually."
        style={{ marginTop: "34px" }}
      />

      <div style={serviceExplorer}>
        {servicesByCategory.map((group) => (
          <section key={group.category} style={categorySection}>
            <div style={categoryHeader} className="home-category-header">
              <h3 style={categoryTitle} className="home-category-title">{group.category}</h3>
              <div style={categoryCount}>{group.services.length} services</div>
            </div>

            <div style={shortcutGrid}>
              {group.services.map((item) => (
                <ServiceShortcutCard
                  key={item.title}
                  icon={item.icon}
                  title={item.title}
                  subtitle={buildServiceSubtitle(item)}
                  tags={item.tags}
                  onClick={() => navigate(item.path)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <SectionHeader
        title="Quick actions"
        subtitle="High-intent product entry points customers often need before or after booking."
        style={{ marginTop: "38px" }}
      />

      <div style={actionGrid}>
        {quickActions.map((item) => (
          <ActionCard
            key={item.title}
            icon={item.icon}
            title={item.title}
            description={item.description}
            actionLabel={item.actionLabel}
            onClick={item.onClick}
          />
        ))}
      </div>

      <BottomNav />
    </AppShell>
  );
}

const heroCard = {
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
  border: "1px solid #222",
  borderRadius: "24px",
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
};

const heroGrid = {
  padding: "32px",
  display: "grid",
  gridTemplateColumns: "1.3fr 0.95fr",
  gap: "24px",
  alignItems: "center",
};

const heroBadge = {
  display: "inline-block",
  padding: "8px 14px",
  borderRadius: "999px",
  border: "1px solid #2a2a2a",
  background: "#0d0d0d",
  color: "#d1d1d1",
  fontSize: "13px",
  marginBottom: "18px",
};

const heroTitle = {
  margin: 0,
  fontSize: "42px",
  lineHeight: 1.1,
};

const heroActions = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  marginTop: "18px",
};

const heroShortcutGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "14px",
};

const heroShortcutBtn = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "16px",
  background: "#0f0f0f",
  color: "#fff",
  border: "1px solid #222",
  borderRadius: "18px",
  cursor: "pointer",
  textAlign: "left",
};

const heroShortcutIcon = {
  fontSize: "22px",
  lineHeight: 1,
};

const heroShortcutLabel = {
  fontWeight: "bold",
  fontSize: "15px",
};

const constructionGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "12px",
  marginBottom: "8px",
};

const professionalHighlightCard = {
  background: "linear-gradient(135deg, #141414, #0d0d0d)",
  border: "1px solid #262626",
  borderRadius: "22px",
  padding: "22px",
  boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
};

const professionalHighlightHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: "18px",
};

const professionalLabel = {
  color: "#f2cf88",
  fontSize: "12px",
  fontWeight: "bold",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "8px",
};

const professionalTitle = {
  margin: 0,
  fontSize: "24px",
  lineHeight: "1.35",
};

const professionalBtn = {
  padding: "12px 18px",
  border: "1px solid #3a3a3a",
  background: "#111",
  color: "#fff",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: "bold",
};

const professionalGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "14px",
};

const serviceExplorer = {
  display: "grid",
  gap: "22px",
};

const categorySection = {
  background: "#111",
  border: "1px solid #222",
  borderRadius: "20px",
  padding: "22px",
};

const categoryHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: "16px",
};

const categoryTitle = {
  margin: 0,
  fontSize: "22px",
};

const categoryCount = {
  color: "#a8a8a8",
  fontSize: "13px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  borderRadius: "999px",
  padding: "8px 12px",
};

const shortcutGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "14px",
};

const actionGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "16px",
};

const journeyGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "16px",
};

const solidBtn = {
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const outlineBtn = {
  padding: "12px 18px",
  border: "1px solid #fff",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

export default Home;
